import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useInvestigators, useCreateInvestigator } from "@/api/hooks/investigators";
import { useAssignSiteDAInvestigator } from "@/api/hooks/siteDAInvestigators";

type Role = "pi" | "sub_investigator";
type Mode = "existing" | "new";

interface Props {
  open: boolean;
  onClose: () => void;
  siteId: string;
  daId: string;
  daCode: string;
  role: Role;
  /** When role === "pi" and a PI is already assigned, the assign call
   * uses replace=true and the reason is required. */
  hasExistingPI: boolean;
}

export function AssignInvestigatorDialog({
  open,
  onClose,
  siteId,
  daId,
  daCode,
  role,
  hasExistingPI,
}: Props) {
  const investigators = useInvestigators();
  const createInvestigator = useCreateInvestigator();
  const assign = useAssignSiteDAInvestigator(siteId, daCode);

  const [mode, setMode] = useState<Mode>("existing");
  const [selectedId, setSelectedId] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [npi, setNpi] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isPiReplace = role === "pi" && hasExistingPI;

  const eligible = useMemo(
    () => (investigators.data ?? []).filter((i) => i.is_active),
    [investigators.data],
  );

  const reset = () => {
    setMode("existing");
    setSelectedId("");
    setFirst("");
    setLast("");
    setEmail("");
    setNpi("");
    setReason("");
    setError(null);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const onConfirm = async () => {
    setError(null);
    if (isPiReplace && !reason.trim()) {
      setError("Reason is required when replacing the current PI.");
      return;
    }
    setSaving(true);
    try {
      let investigatorId = selectedId;
      if (mode === "new") {
        if (!first.trim() || !last.trim()) {
          setError("First and last name are required.");
          setSaving(false);
          return;
        }
        const created = await createInvestigator.mutateAsync({
          first_name: first.trim(),
          last_name: last.trim(),
          email: email.trim() || null,
          npi: npi.trim() || null,
        });
        investigatorId = created.id;
      }
      if (!investigatorId) {
        setError("Pick an investigator or fill out the new-investigator form.");
        setSaving(false);
        return;
      }
      await assign.mutateAsync({
        daId,
        investigatorId,
        role,
        replace: isPiReplace,
        reason: isPiReplace ? reason.trim() : undefined,
      });
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to assign");
    } finally {
      setSaving(false);
    }
  };

  const title =
    role === "pi"
      ? hasExistingPI
        ? "Replace PI"
        : "Assign PI"
      : "Add Sub-investigator";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            size="small"
            variant={mode === "existing" ? "contained" : "outlined"}
            onClick={() => setMode("existing")}
          >
            Existing investigator
          </Button>
          <Button
            size="small"
            variant={mode === "new" ? "contained" : "outlined"}
            onClick={() => setMode("new")}
          >
            New investigator
          </Button>
        </Box>

        {mode === "existing" ? (
          eligible.length === 0 ? (
            <Typography color="text.secondary">
              No active investigators yet. Switch to "New investigator" to create one.
            </Typography>
          ) : (
            <TextField
              select
              fullWidth
              label="Investigator"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {eligible.map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  {i.first_name} {i.last_name}
                  {i.npi ? ` · NPI ${i.npi}` : ""}
                  {i.email ? ` · ${i.email}` : ""}
                </MenuItem>
              ))}
            </TextField>
          )
        ) : (
          <Stack spacing={2}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              <TextField
                required
                label="First name"
                value={first}
                onChange={(e) => setFirst(e.target.value)}
              />
              <TextField
                required
                label="Last name"
                value={last}
                onChange={(e) => setLast(e.target.value)}
              />
            </Box>
            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              label="NPI"
              value={npi}
              onChange={(e) => setNpi(e.target.value)}
              helperText="Optional; required for billable US investigators."
            />
          </Stack>
        )}

        {isPiReplace && (
          <TextField
            label="Reason for replacement"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            sx={{ mt: 2 }}
            helperText="Recorded on the audit log."
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={
            saving ||
            (mode === "existing" && !selectedId) ||
            (mode === "new" && (!first.trim() || !last.trim())) ||
            (isPiReplace && !reason.trim())
          }
        >
          {saving ? "Saving…" : isPiReplace ? "Replace" : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
